/**
 * Sapp's Fitness Hero Section
 * Design: Crimson red + golden yellow + near-black + white
 * Layout: True full-viewport split — text left, photo right
 */
import { useEffect, useRef, useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { scrollToContactForm } from "@/lib/contactFormNavigation";
import { scrollToSection } from "@/lib/sectionNavigation";

const PHOTO = "https://d2xsxph8kpxj0f.cloudfront.net/310519663609231640/YdbhPpnd6XebqcXTfKnGos/hero_fitness_main-DQczbdt4NuCP4tQEiemqJ4.webp";
const MOBILE_PHOTO = "/manus-storage/hero-mobile-640_18319b81.webp";

const RED = "oklch(0.42 0.20 22)";
const RED_HOVER = "oklch(0.55 0.20 22)";
const GOLD = "oklch(0.80 0.17 80)";
const BLACK = "oklch(0.14 0.02 40)";
const RED_PALE = "oklch(0.95 0.03 22)";

const stats = [
  { value: "20+", label: "Years of Experience" },
  { value: "1,000+", label: "Lives Transformed" },
  { value: "#1", label: "Rated Gym" },
  { value: "5.0★", label: "Star Coaching" },
];

const trustPoints = [
  "In-Person & Online Options",
  "All Fitness Levels Welcome",
];

export default function HeroSection() {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      contentRef.current?.querySelectorAll(".hero-animate").forEach((el, i) => {
        const elem = el as HTMLElement;
        elem.style.transition = `opacity 0.55s cubic-bezier(0.23,1,0.32,1) ${i * 0.1}s, transform 0.55s cubic-bezier(0.23,1,0.32,1) ${i * 0.1}s`;
        elem.style.opacity = "1";
        elem.style.transform = "translateY(0)";
      });
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const updateViewport = () => setIsMobile(mediaQuery.matches);
    updateViewport();
    mediaQuery.addEventListener("change", updateViewport);
    return () => mediaQuery.removeEventListener("change", updateViewport);
  }, []);

  return (
    <section id="home" style={{ background: "white" }}>
      {/* HERO ROW */}
      <div
        style={{
          marginTop: "88px",
          width: "100%",
          height: "calc(100vh - 88px)",
          minHeight: "560px",
          display: "flex",
          alignItems: "stretch",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Crimson left accent bar */}
        <div style={{
          position: "absolute",
          left: 0, top: 0, bottom: 0,
          width: "6px",
          background: RED,
          zIndex: 10,
          flexShrink: 0,
        }} />

        {/* LEFT — text content */}
        <div
          ref={contentRef}
          className="hero-content"
          style={{
            width: "52%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            padding: "60px 4% 60px 5%",
            background: "white",
            position: "relative",
            zIndex: 5,
            boxSizing: "border-box",
          }}
        >
          <div style={{ width: "100%" }}>
            {/* Eyebrow badge */}
            <div
              className="hero-animate"
              style={{
                opacity: 0, transform: "translateY(14px)",
                display: "inline-flex", alignItems: "center", gap: "8px",
                padding: "8px 18px", borderRadius: "999px",
                background: RED_PALE,
                border: `1.5px solid oklch(0.42 0.20 22 / 0.35)`,
                marginBottom: "28px",
              }}
            >
              <span style={{
                width: "9px", height: "9px", borderRadius: "50%",
                background: RED, flexShrink: 0, display: "inline-block",
              }} />
              <span style={{
                fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800,
                fontSize: "clamp(0.8rem, 1vw, 1rem)", letterSpacing: "0.14em", textTransform: "uppercase",
                color: RED,
              }}>
                20+ Years of Proven Results
              </span>
            </div>

            {/* Headline */}
            <h1
              className="hero-animate"
              style={{
                opacity: 0, transform: "translateY(14px)",
                fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900,
                fontSize: "clamp(2.2rem, 3.6vw, 4.6rem)", lineHeight: 1.0,
                color: BLACK, marginBottom: "12px", textTransform: "uppercase",
              }}
            >
              <span style={{ display: "block", whiteSpace: "nowrap" }}>The Hardest Part Is</span>
              <span style={{ display: "block", whiteSpace: "nowrap", color: RED }}>Just Getting Started.</span>
            </h1>

            {/* Sub-headline */}
            <h2
              className="hero-animate"
              style={{
                opacity: 0, transform: "translateY(14px)",
                fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800,
                fontSize: "clamp(1.1rem, 1.8vw, 2rem)", lineHeight: 1.15,
                color: "oklch(0.30 0.02 40)", marginBottom: "20px", textTransform: "uppercase",
              }}
            >
              Feel Supported. Get Results.
            </h2>

            {/* Description */}
            <p
              className="hero-animate"
              style={{
                opacity: 0, transform: "translateY(14px)",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "clamp(1rem, 1.3vw, 1.25rem)",
                lineHeight: 1.7, color: "oklch(0.40 0.02 40)",
                marginBottom: "28px",
              }}
            >
              We provide highly customized personal training, nutrition coaching, and accountability to guarantee your transformation.
            </p>

            {/* Trust points */}
            <div
              className="hero-animate"
              style={{
                opacity: 0, transform: "translateY(14px)",
                display: "flex", flexDirection: "column", gap: "12px", marginBottom: "36px",
              }}
            >
              {trustPoints.map((point) => (
                <div key={point} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <CheckCircle2
                    size={22}
                    style={{ color: RED, flexShrink: 0 }}
                  />
                  <span style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: "clamp(0.95rem, 1.1vw, 1.15rem)",
                    fontWeight: 600, color: "oklch(0.32 0.02 40)",
                  }}>
                    {point}
                  </span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div
              className="hero-animate"
              style={{
                opacity: 0, transform: "translateY(14px)",
                display: "flex", flexWrap: "wrap", gap: "16px",
              }}
            >
              <button
                type="button"
                onClick={scrollToContactForm}
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900,
                  fontSize: "clamp(1rem, 1.4vw, 1.3rem)", letterSpacing: "0.08em", textTransform: "uppercase",
                  background: RED, color: "white",
                  border: "none", borderRadius: "8px",
                  padding: "clamp(14px, 1.5vw, 20px) clamp(24px, 2.5vw, 40px)",
                  cursor: "pointer", display: "flex", alignItems: "center", gap: "10px",
                  boxShadow: `0 4px 20px oklch(0.42 0.20 22 / 0.40)`,
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = RED_HOVER;
                  (e.currentTarget as HTMLElement).style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = RED;
                  (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
                }}
              >
                Book a Free Call <ArrowRight size={20} />
              </button>
              <button
                type="button"
                onClick={() => scrollToSection("#programs")}
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800,
                  fontSize: "clamp(1rem, 1.4vw, 1.3rem)", letterSpacing: "0.08em", textTransform: "uppercase",
                  background: "transparent", color: BLACK,
                  border: `2.5px solid ${BLACK}`, borderRadius: "8px",
                  padding: "clamp(12px, 1.3vw, 18px) clamp(20px, 2vw, 34px)",
                  cursor: "pointer", transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = BLACK;
                  (e.currentTarget as HTMLElement).style.color = "white";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "transparent";
                  (e.currentTarget as HTMLElement).style.color = BLACK;
                }}
              >
                See Our Programs
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT — photo */}
        <div
          className="hero-media"
          style={{
            flex: 1,
            position: "relative",
            overflow: "hidden",
            minHeight: "500px",
            alignSelf: "stretch",
          }}
        >
          <img
            src={isMobile ? MOBILE_PHOTO : PHOTO}
            alt="Sapp's Fitness athlete training in gym"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            style={{
              position: "absolute", inset: 0,
              width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "50% 20%",
              display: "block",
            }}
          />
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(to top, rgba(20,5,5,0.55) 0%, transparent 50%)",
            pointerEvents: "none",
          }} />
          <div className="hero-badge" style={{
            position: "absolute", bottom: "24px", left: "24px",
            background: GOLD,
            borderRadius: "6px", padding: "8px 18px",
          }}>
            <span style={{
              fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800,
              fontSize: "clamp(0.85rem, 1vw, 1rem)", letterSpacing: "0.1em", textTransform: "uppercase",
              color: BLACK,
            }}>Real Training. Real Results.</span>
          </div>
        </div>
      </div>

      {/* Mobile overrides */}
      <style>{`
        @media (max-width: 767px) {
          .hero-badge {
            display: none !important;
          }
          #home > div:first-child {
            margin-top: 72px !important;
            height: auto !important;
            min-height: auto !important;
            flex-direction: column !important;
          }
          #home .hero-content {
            width: 100% !important;
            padding: 36px 20px 40px 26px !important;
            order: 2;
          }
          #home .hero-media {
            width: 100% !important;
            flex: none !important;
            height: 60vw !important;
            min-height: 260px !important;
            max-height: 380px !important;
            order: 1;
          }
          #home h1 span {
            white-space: normal !important;
          }
          #home .hero-animate:has(button) {
            width: 100%;
          }
          #home .hero-animate:has(button) > button {
            width: 100%;
            justify-content: center;
          }
          #home .hero-stats {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            padding: 0 12px !important;
          }
          #home .hero-stat:nth-child(even) {
            border-right: none !important;
          }
        }
        @media (min-width: 768px) and (max-width: 1099px) {
          #home .hero-content {
            width: 55% !important;
            padding: 48px 4% 48px 4% !important;
          }
        }
      `}</style>

      {/* Stats bar — near-black with gold accents */}
      <div style={{ background: BLACK, padding: "24px 0" }}>
        <div
          className="hero-stats"
          style={{
            width: "100%",
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            padding: "0 4%",
            boxSizing: "border-box",
          }}
        >
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className="hero-stat"
              style={{
                textAlign: "center",
                padding: "14px 16px",
                borderRight: i < stats.length - 1 ? "1px solid rgba(255,255,255,0.12)" : "none",
              }}
            >
              <div style={{
                fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900,
                fontSize: "clamp(1.8rem, 3.2vw, 3rem)", color: GOLD, lineHeight: 1,
              }}>
                {stat.value}
              </div>
              <div style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "clamp(0.75rem, 0.9vw, 0.95rem)",
                fontWeight: 600,
                color: "rgba(255,255,255,0.65)", textTransform: "uppercase",
                letterSpacing: "0.06em", marginTop: "5px",
              }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
